/**
 * Dynamic-field tokens for design TextBlock/WatermarkBlock content, e.g.
 * `{{recruiter.name}}`. Resolved at render time (both the client live
 * preview and the server Puppeteer render) so a design authored once keeps
 * showing the correct per-session recruiter, rather than baking one
 * recruiter's contact info permanently into the box.
 *
 * Supported namespaces:
 * - `recruiter.*` — resolved from the current user's per-agency profile
 *   (User.agencyProfiles[agencyId]), a genuinely live per-session source.
 * - `agency.name` — resolved from Agency.name, live across renames.
 * - `agency.phone|url|email|linkedIn` — resolved from the agency contact
 *   details captured at onboarding (persisted in the legacy
 *   `preferences.customize` seed) or, on the public trial page, from the
 *   trial form values. These exist so the checked-in DEFAULT_DESIGN can
 *   bind the trial form / onboarding fields without being rebuilt per
 *   keystroke.
 */

const TOKEN_PATTERN = /\{\{\s*([a-zA-Z]+)\.([a-zA-Z]+)\s*\}\}/g

const RECRUITER_FIELDS = ['name', 'phone', 'email', 'linkedIn'] as const
type RecruiterField = (typeof RECRUITER_FIELDS)[number]

const AGENCY_FIELDS = ['name', 'phone', 'url', 'email', 'linkedIn'] as const
type AgencyField = (typeof AGENCY_FIELDS)[number]

export interface TokenResolutionData {
  recruiter?: {
    name?: string | null
    phone?: string | null
    email?: string | null
    linkedIn?: string | null
  }
  agency?: {
    name?: string | null
    phone?: string | null
    url?: string | null
    email?: string | null
    linkedIn?: string | null
  }
}

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function isRecruiterField(field: string): field is RecruiterField {
  return (RECRUITER_FIELDS as readonly string[]).includes(field)
}

function isAgencyField(field: string): field is AgencyField {
  return (AGENCY_FIELDS as readonly string[]).includes(field)
}

/**
 * Replaces `{{namespace.field}}` tokens with HTML-escaped plain text —
 * tokens never resolve to raw HTML, so a value can't reopen the markup it's
 * substituted into. Unresolved/unknown tokens resolve to an empty string.
 */
function substituteTokens(html: string, data: TokenResolutionData): string {
  return html.replace(
    TOKEN_PATTERN,
    (_match, namespace: string, field: string) => {
      let value: string | null | undefined

      if (namespace === 'recruiter' && isRecruiterField(field)) {
        value = data.recruiter?.[field]
      } else if (namespace === 'agency' && isAgencyField(field)) {
        value = data.agency?.[field]
      } else {
        return ''
      }

      return value ? escapeHtml(value) : ''
    }
  )
}

// Non-global twin of TOKEN_PATTERN: `.test()` on a /g regex is stateful
// (lastIndex), so a separate stateless regex is used for containment checks.
const HAS_TOKEN = /\{\{\s*[a-zA-Z]+\.[a-zA-Z]+\s*\}\}/

/** True when, after stripping tags and entities-as-space, nothing remains. */
function isVisuallyEmpty(html: string): boolean {
  return (
    html
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/gi, ' ')
      .trim() === ''
  )
}

/**
 * Resolves `{{namespace.field}}` tokens in `html` and collapses lines that
 * consisted only of tokens that all resolved empty — matching the legacy
 * show-if-present ("showOrInfer") behavior, where an absent field dropped
 * its whole line instead of leaving a blank gap in the band.
 *
 * A "line" is either a top-level non-nested `<div>…</div>` block (what the
 * editor's contenteditable produces) or a `<br/>`-separated segment. Lines
 * mixing static text with an empty-resolving token (e.g. `Phone: {{...}}`)
 * are kept as-is — authored static text is never dropped. Nested-div lines
 * are resolved but never collapsed (conservative: avoids mis-pairing tags).
 */
export function resolveDesignTokens(
  html: string,
  data: TokenResolutionData
): string {
  // Pass A: token-only <div> lines. Non-greedy match pairs each opening tag
  // with the nearest close, which is only correct for non-nested divs — the
  // nested case is detected and left for plain substitution below.
  const divProcessed = html.replace(
    /(<div\b[^>]*>)([\s\S]*?)(<\/div>)/gi,
    (match, open: string, inner: string, close: string) => {
      if (/<div/i.test(inner) || !HAS_TOKEN.test(inner)) return match
      const resolvedInner = substituteTokens(inner, data)
      if (isVisuallyEmpty(resolvedInner)) return ''
      return `${open}${resolvedInner}${close}`
    }
  )

  // Pass B: <br/>-separated lines. Segments without tokens (including
  // markup already resolved in pass A) pass through verbatim.
  const segments = divProcessed.split(/<br\s*\/?>/i)
  if (segments.length === 1) return substituteTokens(divProcessed, data)

  const kept: string[] = []
  for (const segment of segments) {
    if (!HAS_TOKEN.test(segment)) {
      kept.push(segment)
      continue
    }
    const resolved = substituteTokens(segment, data)
    if (isVisuallyEmpty(resolved)) continue
    kept.push(resolved)
  }
  return kept.join('<br/>')
}

/**
 * Builds the resolution data for a render, given the current recruiter's
 * per-agency profile, the agency's canonical name, and (optionally) the
 * agency contact details captured at onboarding.
 */
export function buildTokenResolutionData(
  agencyName: string | null | undefined,
  recruiterProfile?: TokenResolutionData['recruiter'] | null,
  agencyContact?: Omit<
    NonNullable<TokenResolutionData['agency']>,
    'name'
  > | null
): TokenResolutionData {
  return {
    recruiter: recruiterProfile ?? undefined,
    agency: { name: agencyName ?? undefined, ...(agencyContact ?? {}) },
  }
}
